<script setup lang="ts">
import type { DynamicUIDetail } from "../models/DynamicUIDetail.interface";
import type { DynamicUIDetailSpecific } from "../models/DynamicUIDetailSpecific.interface";
import { storeToRefs } from "pinia";
import type LxcContainer from "~/core/components/ContainerComponent.vue";
import type LxcTabsWithNavigation from "~/core/components/TabsWithNavigation.vue";
import type { TabNavigation } from "~/core/components/TabsWithNavigation.vue";
import LxcError from "~/core/models/LxcError.class";
import { useComponentMap } from "~/modules/dtwin/composables/useComponentMap";
import { useFetchDtwinModelUIConfig } from "~/modules/dtwin/composables/useFetchDtwinModelUIConfig";
import {
  PATH_DTWINS_ID_ADDITIONAL_METADATA,
  PATH_DTWINS_ID_CERTIFICATE,
  PATH_DTWINS_ID_CONFIGURATION,
  PATH_DTWINS_ID_DATA_VISUALISATION,
  PATH_DTWINS_ID_DESCRIPTION,
  PATH_DTWINS_ID_FIRMWARE,
  PATH_DTWINS_ID_LOGS,
} from "~/modules/dtwin/constants";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import dtwinsService from "~/modules/dtwin/services/dtwin.service";
import { LicenseFeatureName } from "~/modules/license/models/LicenseFeatureName.enum";
import { useLicenseStore } from "~/modules/license/stores/useLicenseStore";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

const props = defineProps<{
  breadcrumbContainer?: HTMLElement;
  container?: typeof LxcContainer;
}>();

const route = useRoute();
const router = useRouter();

const { componentMap } = useComponentMap();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const licenseStore = useLicenseStore();

const {
  result: deviceModelUiConfig,
  isLoading: isLoadingModelUiConfig,
  fetchData: fetchDeviceModelUiConfig,
} = useFetchDtwinModelUIConfig({
  organizationCode: currentOrganization.value?.code,
});

provide("isLoadingModelUiConfig", isLoadingModelUiConfig);
provide("deviceModelUiConfig", deviceModelUiConfig);

const dtwin: Ref<Dtwin | undefined> = ref();
const dtwinUid: Ref<string> = ref(route.params.id.toString());
const errorDtwin: Ref<LxcError | null> = ref(null);
const isLoadingDtwin = ref(true);
const mainParentElement: ComputedRef<HTMLElement | undefined | null> = computed(
  () => props.breadcrumbContainer?.parentElement,
);
const containerPaddingTop: ComputedRef<number> = computed(
  () => props.container?.paddingTop ?? 0,
);
const containerPaddingBottom: ComputedRef<number> = computed(
  () => props.container?.paddingBottom ?? 0,
);
const tabsContentHeight: Ref<number> = ref(0);
const tabsContentTop: Ref<number> = ref(0);
const parentScrollTop: Ref<number> = ref(0);

/**
 * Dtwin model
 */
const isLoadingDtwinModel = ref(false);
const errorDtwinModel = ref();
const dtwinModel: Ref<DtwinModel | undefined> = ref();

async function fetchDtwinModel() {
  if (!currentOrganization.value?.code) {
    throw new Error("Organization code is undefined");
  }
  if (dtwin.value) {
    errorDtwinModel.value = undefined;

    isLoadingDtwinModel.value = true;

    const response = await dtwinsService.getOrganizationDtwinsModelById(
      currentOrganization.value?.code,
      dtwin.value.deviceModelUid,
    );
    if (LxcError.check(response)) {
      errorDtwinModel.value = response;
    } else {
      dtwinModel.value = response;
    }
    isLoadingDtwinModel.value = false;
  }
}
/**
 * End Dtwin model
 */

async function fetchDtwin() {
  if (!currentOrganization.value?.code) {
    throw new Error("Organization code is undefined");
  }
  const response = await dtwinsService.getOrganizationDtwin(
    currentOrganization.value?.code,
    dtwinUid.value,
    // TODO see if we need to ask backend to get metadata and operation statuses
    true,
    true,
  );

  if (LxcError.check(response)) {
    dtwin.value = undefined;
    errorDtwin.value = response;
  } else {
    dtwin.value = response;
  }

  isLoadingDtwin.value = false;
}

const fetchData = async () => {
  await fetchDtwin();
};

function updateTabContentHeight() {
  let height = 0;
  const tabsHeight = tabsWithNavigationRef?.value?.tabsRef?.tabsHeight;

  if (props.breadcrumbContainer?.parentElement) {
    const containerHeight =
      props.breadcrumbContainer.parentElement.getBoundingClientRect().height -
      props.breadcrumbContainer.offsetHeight;

    if (props.container && tabsHeight !== undefined) {
      height =
        containerHeight -
        containerPaddingTop.value -
        containerPaddingBottom.value -
        tabsHeight;
    }
  }

  tabsContentHeight.value = height;
}

function initTabContentTopPosition() {
  const breadcrumbHeight =
    props.breadcrumbContainer?.getBoundingClientRect().height ?? 0;
  const tabsHeight = tabsWithNavigationRef?.value?.tabsRef?.tabsHeight ?? 0;
  tabsContentTop.value =
    breadcrumbHeight + tabsHeight + containerPaddingTop.value;
}

const onParentScroll = (evt: Event) => {
  const parentElm = evt.target as HTMLElement | undefined;

  if (parentElm && parentElm === mainParentElement.value) {
    parentScrollTop.value = parentElm.scrollTop;
  }
};

const onComponentUpdated = (tabIndex: number) => {
  if (
    tabIndex ===
    tabNavigations.value.findIndex(
      (tabNavigation: TabNavigation) =>
        tabNavigation.id === dataVisualisationTabId,
    )
  ) {
    fetchDtwin();
  }

  updateTabContentHeight();
  initTabContentTopPosition();
};

//
// Navigation section. Logic / configuration responsible for the navigation.
//
const tabsWithNavigationRef: Ref<typeof LxcTabsWithNavigation | undefined> =
  ref();
const descriptionTabId = "description";
const additionalMetadataTabId = "additionalMetadata";
const certificateTabId = "certificate";
const firmwareTabId = "firmware";
const configurationTabId = "configuration";
const dataVisualisationTabId = "dataVisualisation";
const logsTabId = "logs";

const descriptionTabRedirectPath = router.resolve({
  name: PATH_DTWINS_ID_DESCRIPTION,
  params: { id: dtwinUid.value },
}).href;

const additionalMetadataTabRedirectPath = router.resolve({
  name: PATH_DTWINS_ID_ADDITIONAL_METADATA,
  params: { id: dtwinUid.value },
}).href;

const certificateTabRedirectPath = router.resolve({
  name: PATH_DTWINS_ID_CERTIFICATE,
  params: { id: dtwinUid.value },
}).href;

const firmwareTabRedirectPath = router.resolve({
  name: PATH_DTWINS_ID_FIRMWARE,
  params: { id: dtwinUid.value },
}).href;

const configurationTabRedirectPath = router.resolve({
  name: PATH_DTWINS_ID_CONFIGURATION,
  params: { id: dtwinUid.value },
}).href;

const dataVisualisationTabRedirectPath = router.resolve({
  name: PATH_DTWINS_ID_DATA_VISUALISATION,
  params: { id: dtwinUid.value },
}).href;

const logsTabRedirectPath = router.resolve({
  name: PATH_DTWINS_ID_LOGS,
  params: { id: dtwinUid.value },
}).href;

const tabNavigations = computed(() => {
  const tabNavigations: TabNavigation[] = [
    {
      id: descriptionTabId,
      redirectPath: descriptionTabRedirectPath,
      labelTranslationKey: "dtwins.form.description.title",
    },
  ];

  if (
    deviceModelUiConfig.value?.details.additionalMetadata &&
    (!deviceModelUiConfig.value?.details.additionalMetadata
      .requiredLicenseFeature ||
      (!Array.isArray(deviceModelUiConfig.value?.details.additionalMetadata) &&
        licenseStore.isLicenseFeatureEnabled(
          deviceModelUiConfig.value?.details.additionalMetadata
            .requiredLicenseFeature as string,
        )))
  ) {
    tabNavigations.push({
      id: additionalMetadataTabId,
      redirectPath: additionalMetadataTabRedirectPath,
      labelTranslationKey: "dtwins.form.additionalMetadata.title",
    });
  }

  if (deviceModelUiConfig.value?.details.certificate) {
    tabNavigations.push({
      id: certificateTabId,
      redirectPath: certificateTabRedirectPath,
      labelTranslationKey: "dtwins.form.certificate.title",
    });
  }

  if (deviceModelUiConfig.value?.details.firmware) {
    tabNavigations.push({
      id: firmwareTabId,
      redirectPath: firmwareTabRedirectPath,
      labelTranslationKey: "dtwins.form.firmware.title",
    });
  }

  if (deviceModelUiConfig.value?.details.configuration) {
    tabNavigations.push({
      id: configurationTabId,
      redirectPath: configurationTabRedirectPath,
      labelTranslationKey: "dtwins.form.configuration.title",
    });
  }

  // Add tab dataviz if activated from model UI and activated by license
  if (
    deviceModelUiConfig.value?.details.dataviz &&
    licenseStore.isLicenseFeatureEnabled(LicenseFeatureName.DATAVIZ_DEVICE)
  ) {
    tabNavigations.push({
      id: dataVisualisationTabId,
      redirectPath: dataVisualisationTabRedirectPath,
      labelTranslationKey: "dtwins.form.dataviz.title",
    });
  }

  // Add tab logs if activated from model UI
  if (deviceModelUiConfig.value?.details.logs) {
    tabNavigations.push({
      id: logsTabId,
      redirectPath: logsTabRedirectPath,
      labelTranslationKey: "dtwins.form.logs.title",
    });
  }

  return tabNavigations;
});

//
// Lifecycle Hooks. See: https://vuejs.org/api/composition-api-lifecycle
//
onUpdated(onComponentUpdated);

onMounted(async () => {
  await fetchData();
  await fetchDtwinModel();
  await fetchDeviceModelUiConfig(dtwin.value?.deviceModelUid);

  const parentElm = mainParentElement.value;
  if (parentElm) {
    parentElm.addEventListener("scroll", onParentScroll);
  }
  window.addEventListener("resize", updateTabContentHeight);
});
onBeforeUnmount(() => {
  const parentElm = mainParentElement.value;

  if (parentElm) {
    parent.removeEventListener("scroll", onParentScroll);
  }
  window.removeEventListener("resize", updateTabContentHeight);
});

defineExpose({
  dtwin,
  dtwinModel,
  fetchData,
  tabNavigations,
});
</script>

<template>
  <container-component
    :px="0"
    :py="0"
    :error="errorDtwin || errorDtwinModel"
    :is-loading="
      isLoadingDtwin || isLoadingModelUiConfig || isLoadingDtwinModel
    "
  >
    <tabs-with-navigation
      ref="tabsWithNavigationRef"
      :tab-navigations="tabNavigations"
      @update:selected-tab-index="onComponentUpdated"
    >
      <template #[descriptionTabId]>
        <dtwin-form-description
          v-model:dtwin="dtwin"
          :device-model-ui-config="deviceModelUiConfig"
        />
      </template>
      <template #[certificateTabId]>
        <dtwin-form-certificate
          v-if="currentOrganization"
          :organization-code="currentOrganization?.code"
          :dtwin="dtwin"
          :dtwin-model="dtwinModel"
          :device-model-ui-config="
            deviceModelUiConfig?.details.certificate as Record<
              string,
              Record<string, DynamicUIDetail[]>
            >
          "
        />
      </template>
      <template #[additionalMetadataTabId]>
        <component
          :is="
            componentMap[
              (
                deviceModelUiConfig.details
                  .additionalMetadata as DynamicUIDetailSpecific
              ).componentId as string
            ]
          "
          v-if="deviceModelUiConfig?.details.additionalMetadata?.componentId"
          :dtwin="dtwin"
        />
      </template>
      <template #[firmwareTabId]>
        <dtwin-form-firmware
          v-if="currentOrganization"
          :organization-code="currentOrganization?.code"
          :dtwin="dtwin"
          :dtwin-model="dtwinModel"
          :ui-config-firmware="
            deviceModelUiConfig?.details.firmware as Record<
              string,
              Record<string, DynamicUIDetail[]>
            >
          "
        />
      </template>
      <template #[configurationTabId]>
        <dtwin-form-configuration
          :dtwin="dtwin"
          :device-model-ui-config="deviceModelUiConfig"
          :dtwin-model="dtwinModel"
        />
      </template>
      <template #[dataVisualisationTabId]>
        <dtwin-form-dataviz
          :dtwin="dtwin"
          :offset-top="tabsContentTop"
          :scroll-top="parentScrollTop"
          :tabs-content-height="tabsContentHeight"
        />
      </template>
      <template #[logsTabId]>
        <dtwin-form-logs :dtwin-uid="dtwin?.uid" />
      </template>
    </tabs-with-navigation>
  </container-component>
</template>
